import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp, scrollToId } from "./Reveal";

const MOCKUP_IMG =
  "https://static.prod-images.emergentagent.com/jobs/5c36542e-d0de-465a-8b30-ac5194369ef2/images/fb24ddce751399a1c949a9f60d0751994614341ad4a9ea033838d97f1854a328.jpeg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden flex items-center bg-[#050505]">
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 z-0 overflow-hidden"
        data-testid="hero-mockup-frame"
      >
        <motion.img
          data-testid="hero-mockup-image"
          src={MOCKUP_IMG}
          alt="Black MacBook showing a client growth dashboard"
          className="absolute inset-0 h-full w-full object-cover"
          animate={{ scale: [1.02, 1.07, 1.02] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
      <div className="absolute inset-0 z-[2] pointer-events-none bg-[linear-gradient(90deg,rgba(5,5,5,0.95)_0%,rgba(5,5,5,0.75)_45%,rgba(5,5,5,0.4)_100%)]" />
      <div className="absolute inset-0 z-[2] pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,5,5,0.5)_100%)]" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-32 pb-28">
        <motion.div style={{ y: textY }} className="max-w-3xl">
          <MaskedLines
            as="h1"
            delay={0.25}
            className="font-display font-light text-[clamp(3.1rem,8vw,7rem)] leading-[0.98] tracking-tight"
            lines={[
              { content: "Built to scale." },
              {
                content: (
                  <span className="gold-text italic">Designed to win.</span>
                ),
              },
            ]}
          />
          <FadeUp delay={0.75}>
            <p className="mt-8 text-lg sm:text-2xl font-light tracking-tight text-neutral-200">
              Ads that scale. Sites that sell. One studio.
            </p>
          </FadeUp>
          <FadeUp delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("what-i-do")}
              className="group flex items-center gap-3 rounded-full bg-[#D4AF37] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black transition-[background-color,box-shadow,transform] duration-300 hover:bg-[#E5C158] hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
            >
              See what we do
              <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              data-testid="hero-cta-secondary"
              href="mailto:CFDigiCo@gmail.com"
              className="group flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-[border-color,color,box-shadow] duration-300 hover:border-[#D4AF37]/60 hover:text-[#E5C158]"
            >
              Inquire
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </FadeUp>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-6 sm:left-10 lg:left-16 z-10 hidden sm:flex items-center gap-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          Scroll
        </span>
        <span className="scroll-cue-line block h-10 w-px bg-[#D4AF37]/70" />
      </div>
    </section>
  );
}